<template>
  <div>
    <v-tabs
      v-if="hasStatusHistory"
      v-model="activeHistoryTab"
      class="mb-4"
      color="primary"
      density="compact"
      slider-color="blue-darken-3">
      <v-tab value="changes">Історія змін</v-tab>
      <v-tab value="statuses">Історія статусів</v-tab>
    </v-tabs>

    <v-window v-model="activeHistoryTab">
      <v-window-item value="changes">
        <b-section-loader :loading="$loading.isLoading('getHistory')">
          <v-data-table
            v-if="historyList.length"
            :headers="historyHeaders"
            :hide-default-footer="!historyList.length"
            :hide-default-header="!historyList.length"
            :items="historyList"
            :items-per-page="50">
            <template #item.userName="{ item }">
              {{ getPersonName(item.userName, { initials: true }) || '---' }}
            </template>
            <template #item.date="{ item }">
              {{
                new CustomDate(item?.dateTo).toString({ time: true }) || '---'
              }}
            </template>
            <template #item.field="{ item }">
              {{ item?.name || '---' }}
            </template>
            <template #item.changedValue="{ item }">
              <div class="d-flex align-center ga-2">
                <history-item :value="item ? item.oldValue : null">
                </history-item>
                <v-icon>mdi-arrow-right-thin</v-icon>
                <history-item :value="item ? item.newValue : null">
                </history-item>
              </div>
            </template>
          </v-data-table>
          <empty-section
            v-else
            description="Нових записів наразі не було"
            title="Історія змін порожня"></empty-section>
        </b-section-loader>
      </v-window-item>

      <v-window-item v-if="hasStatusHistory" value="statuses">
        <status-history-list
          :items="statusHistoryList"
          :loading="$loading.isLoading('getStatusHistory')">
        </status-history-list>
      </v-window-item>
    </v-window>
  </div>
</template>

<script lang="ts">
import HistoryItem from '@/components/history/HistoryItem.vue'
import StatusHistoryList from '@/components/history/StatusHistoryList.vue'
import { BSectionLoader } from 'best-modules/components'
import { handleAsync } from 'best-modules/plugins'
import { CustomDate, getPersonName } from 'best-modules/utils'
import { historyHeaders } from '@/utils/tableHeaders'
import { computed, ref, watch, type PropType } from 'vue'
import { ENTITY_TYPE } from '@/utils/constants'
import type {
  HistoryRow,
  StatusHistoryRequest,
  StatusHistoryRow,
} from '@/requests/history'
import { orderHistory } from '@/requests/history'
import EmptySection from '@/components/EmptySection.vue'

export default {
  name: 'HistoryList',
  components: { EmptySection, HistoryItem, StatusHistoryList, BSectionLoader },
  props: {
    entityId: {
      type: [Number, String],
      required: true,
    },
    entityTypeId: {
      type: Number as PropType<number | null>,
      default: null,
    },
    statusHistoryRequest: {
      type: Function as PropType<StatusHistoryRequest>,
      default: null,
    },
    statusHistoryRefreshKey: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      historyHeaders,
    }
  },
  methods: { getPersonName, CustomDate },
  setup(props) {
    const historyList = ref<HistoryRow[]>([])
    const statusHistoryList = ref<StatusHistoryRow[]>([])
    const activeHistoryTab = ref('changes')
    const hasStatusHistory = computed(() => Boolean(props.statusHistoryRequest))

    const getHistoryRequest = () => {
      switch (props.entityTypeId) {
        case ENTITY_TYPE.ORDER:
          return orderHistory(props.entityId)
        default:
          return Promise.resolve([])
      }
    }

    const getHistory = () => {
      return handleAsync('getHistory', () => {
        return getHistoryRequest().then(res => {
          historyList.value = Array.isArray(res) ? res : []
          return historyList.value
        })
      })
    }

    const getStatusHistory = () => {
      if (!props.statusHistoryRequest) return

      return handleAsync('getStatusHistory', () => {
        return props.statusHistoryRequest(props.entityId).then(res => {
          statusHistoryList.value = Array.isArray(res) ? res : []
          return statusHistoryList.value
        })
      })
    }

    getHistory()
    getStatusHistory()

    watch(
      () => props.statusHistoryRefreshKey,
      () => {
        getStatusHistory()
      }
    )

    return {
      activeHistoryTab,
      getHistory,
      getStatusHistory,
      hasStatusHistory,
      historyList,
      statusHistoryList,
    }
  },
}
</script>

<style lang="scss" scoped></style>
